import { Toast } from './AppToast';
import localforage from 'localforage';
import moment from 'moment';
import 'moment/locale/es';
moment.locale('es');
import { apiGet } from '../utils/Api';

/**
 * Clase para manejo de base de datos local (IndexedDB)
 * Implementa patrón Singleton
 * @class AppDB
 */
class AppDB {
    /**
     * Constructor del AppDB (Singleton)
     */
    constructor() {
        if (AppDB._instance) {
            return AppDB._instance;
        }

        /** @type {boolean} */
        this.initialized = false;
        /** @type {AppManager|null} */
        this.app = null;

        /** @type {number|null} */
        this.currentTripId = null;
        /** @type {Object|null} */
        this.member = null;
        /** @type {Object|null} */
        this.applications = null;
        /** @type {Object|null} */
        this.trip = null;
        /** @type {Object|null} */
        this.subprogram = null;

        AppDB._instance = this;

        localforage.config({
            driver: localforage.INDEXEDDB,
            name: 'WETAG',
            version: 1.0,
            storeName: 'main'
        });
    }

    /**
     * Inicializa el módulo de base de datos
     * @async
     * @param {AppManager} app - Instancia de AppManager
     * @returns {Promise<void>}
     * @throws {Error} Si hay error en la inicialización
     */
    /**
     * Inicializa el módulo de base de datos
     * @async
     * @param {AppManager} app - Instancia de AppManager
     * @returns {Promise<void>}
     * @throws {Error} Si hay error en la inicialización
     */
    async init(app) {
        if (this.initialized) {
            return;
        }

        try {
            this.app = app;

            const isAuthenticated = app.auth && await app.auth.isAuthenticated();

            if (isAuthenticated) {
                try {
                    await this.getMember();
                } catch (error) {
                    throw error;
                }

                try {
                    await this.getApplications();
                } catch (error) {
                    throw error;
                }

                try {
                    await this.getCurrentTripId();
                } catch (error) {
                    throw error;
                }

                try {
                    await this.getTrip();
                } catch (error) {
                    throw error;
                }
            }

            this.initialized = true;
        } catch (error) {
            this.initialized = true;
            throw error;
        }
    }


    /**
     * Obtiene los datos del miembro (con cache)
     * @async
     * @param {boolean} [reload=false] - Forzar recarga desde servidor
     * @param {number|false} [memberId=false] - ID del miembro (opcional)
     * @returns {Promise<Object>} Datos del miembro
     * @throws {Error} Si hay error al obtener datos o no se encuentra memberId
     */
    async getMember(reload = false, memberId = false) {
        try {
            const table = await localforage.getItem('member');
            const expired = table ? moment().diff(moment(table.timestamp), 'days', true) > 1 : true;

            if (table && !reload && !expired) {
                this.member = table.data;
                return table.data;
            }

            memberId = memberId || this.member?.member_id || (table && table.data.member_id);
            if (!memberId) {
                throw new Error('No Member ID');
            }

            const results = await apiGet(`/member/${memberId}?reload`);

            if (results.class === 'success') {
                await this.updateMember(results.data);
                return results.data;
            } else {
                Toast.show(results.html, results.class);
                throw new Error(results.html);
            }
        } catch (error) {
            throw error;
        }
    }

    /**
     * Actualiza el member haciendo merge en this.member y localforage
     * @async
     * @param {Object} memberData - Datos del member a actualizar
     * @returns {Promise<void>}
     */
    async updateMember(memberData) {
        if (!memberData) return;

        const memberTable = await localforage.getItem('member');
        if (memberTable && memberTable.data) {
            Object.assign(memberTable.data, memberData);
            memberTable.timestamp = moment().format();
            await localforage.setItem('member', memberTable);
        } else {
            await localforage.setItem('member', {
                timestamp: moment().format(),
                data: memberData
            });
        }

        if (this.member) {
            Object.assign(this.member, memberData);
        } else {
            this.member = memberData;
        }

        if (memberData.applications) {
            this.applications = memberData.applications;
            await localforage.setItem('applications', {
                timestamp: moment().format(),
                data: memberData.applications
            });
        }
    }


    /**
     * Obtiene las applications (con cache)
     * @async
     * @param {boolean} [reload=false] - Forzar recarga desde servidor
     * @param {number|false} [memberId=false] - ID del miembro (opcional)
     * @returns {Promise<Object>} Datos de las applications (keyed by application_id)
     * @throws {Error} Si hay error al obtener datos o no se encuentra memberId
     */
    async getApplications(reload = false, memberId = false) {
        try {
            const table = await localforage.getItem('applications');
            const expired = table ? moment().diff(moment(table.timestamp), 'hours', true) > 1 : true;

            if (table && !reload && !expired) {
                this.applications = table.data;
                return table.data;
            }

            memberId = memberId || this.member?.member_id;
            if (!memberId) {
                const memberTable = await localforage.getItem('member');
                memberId = memberTable?.data?.member_id;
            }
            if (!memberId) {
                throw new Error('No Member ID');
            }

            const results = await apiGet(`/member/${memberId}/applications?reload`);

            if (results.class === 'success') {
                await this.updateApplications(results.data);
                return results.data;
            } else {
                Toast.show(results.html, results.class);
                throw new Error(results.html);
            }
        } catch (error) {
            throw error;
        }
    }

    /**
     * Actualiza las applications haciendo merge en localforage usando application_id como índice
     * @async
     * @param {Object} applicationsData - Objeto con las applications a actualizar (keyed by application_id)
     * @returns {Promise<void>}
     */
    async updateApplications(applicationsData) {
        if (!applicationsData || typeof applicationsData !== 'object') return;

        let applicationsTable = await localforage.getItem('applications');
        if (!applicationsTable) {
            applicationsTable = {
                timestamp: moment().format(),
                data: {}
            };
        }
        if (!applicationsTable.data) {
            applicationsTable.data = {};
        }

        Object.values(applicationsData).forEach(applicationData => {
            const applicationId = applicationData.application_id;
            if (applicationId) {
                applicationsTable.data[applicationId] = applicationData;
            }
        });

        applicationsTable.timestamp = moment().format();
        await localforage.setItem('applications', applicationsTable);

        if (this.applications) {
            Object.assign(this.applications, applicationsData);
        } else {
            this.applications = applicationsData;
        }
    }

    /**
     * Actualiza una application haciendo merge en localforage usando application_id como índice
     * @async
     * @param {Object} applicationData - Datos de la application a actualizar (debe incluir application_id)
     * @returns {Promise<void>}
     */
    async updateApplication(applicationData) {
        if (!applicationData || !applicationData.application_id) return;

        let applicationsTable = await localforage.getItem('applications');
        if (!applicationsTable) {
            applicationsTable = {
                timestamp: moment().format(),
                data: {}
            };
        }
        if (!applicationsTable.data) {
            applicationsTable.data = {};
        }

        const applicationId = applicationData.application_id;
        if (applicationsTable.data[applicationId]) {
            Object.assign(applicationsTable.data[applicationId], applicationData);
        } else {
            applicationsTable.data[applicationId] = applicationData;
        }
        applicationsTable.timestamp = moment().format();
        await localforage.setItem('applications', applicationsTable);
    }


    /**
     * Obtiene el ID del viaje actual
     * @async
     * @param {number|false} [tripId=false] - ID del viaje a usar directamente
     * @returns {Promise<number|false>} ID del viaje actual o false si no está disponible
     * @throws {Error} Si hay error al obtener el trip ID
     */
    async getCurrentTripId(tripId = false) {
        try {
            if (tripId) {
                this.setCurrentTripId(tripId);
                return tripId;
            }

            const storedTripId = await localforage.getItem('currentTripId');
            if (storedTripId) {
                this.currentTripId = storedTripId;
                return this.currentTripId;
            }

            if (!this.applications) {
                await this.getApplications();
            }

            const applications = this.applications;
            if (applications && typeof applications === 'object') {
                const applicationsArray = Object.values(applications);
                if (applicationsArray.length > 0) {
                    const applicationsByDate = applicationsArray.sort((a, b) => moment(b.date) - moment(a.date));
                    const lastApplicationId = applicationsByDate[0]?.application_id;
                    if (lastApplicationId) {
                        await this.setCurrentTripId(lastApplicationId);
                        return lastApplicationId;
                    }
                }
            }

            return false;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Establece el ID del viaje actual y carga los datos del viaje
     * @async
     * @param {number} tripId - ID del viaje a establecer
     * @returns {Promise<void>}
     */
    async setCurrentTripId(tripId) {
        if (tripId) {
            const oldTripId = this.currentTripId;
            this.currentTripId = tripId;
            localforage.setItem('currentTripId', parseInt(tripId));

            if (!oldTripId || oldTripId !== tripId) {
                await this.getTrip(true, tripId);
            }
        }
    }

    /**
     * Obtiene los datos del viaje (con cache)
     * @async
     * @param {boolean} [reload=false] - Forzar recarga desde servidor
     * @param {number|false} [tripId=false] - ID del viaje (opcional)
     * @returns {Promise<Object>} Datos del viaje
     * @throws {Error} Si hay error al obtener datos o no se encuentra tripId
     */
    async getTrip(reload = false, tripId = false) {
        try {
            const table = await localforage.getItem('trip');
            const expired = table ? moment().diff(moment(table.timestamp), 'minutes') > 10 : true;

            if (table && !reload && !expired) {
                this.trip = table.data;
                return table.data;
            } else {
                const resolvedTripId = await this.getCurrentTripId(tripId);
                if (resolvedTripId) {
                    const results = await apiGet(`/trip/${resolvedTripId}?reload`);

                    if (results.class === 'success') {
                        await this.updateTrip(results.data);
                        return results.data;
                    } else {
                        Toast.show(results.html, results.class);
                        throw new Error(results.html);
                    }
                } else {
                    if (this.app && this.app.auth) {
                        this.app.auth.logout();
                    }
                    throw new Error('No Trip ID');
                }
            }
        } catch (error) {
            throw error;
        }
    }

    /**
     * Actualiza el trip haciendo merge en this.trip y localforage
     * @async
     * @param {Object} tripData - Datos del trip a actualizar
     * @returns {Promise<void>}
     */
    async updateTrip(tripData) {
        if (!tripData) return;

        const tripTable = await localforage.getItem('trip');
        if (tripTable && tripTable.data) {
            Object.assign(tripTable.data, tripData);
            tripTable.timestamp = moment().format();
            await localforage.setItem('trip', tripTable);
        } else {
            await localforage.setItem('trip', {
                timestamp: moment().format(),
                data: tripData
            });
        }

        if (this.trip) {
            Object.assign(this.trip, tripData);
        } else {
            this.trip = tripData;
        }

        if (tripData.member) {
            await this.updateMember(tripData.member);
        }

        if (tripData.application) {
            await this.updateApplication(tripData.application);
        }
    }

}

export { AppDB };
